import { apiFetch } from './api'
import { supabase } from './supabase'
import type {
  CameraCapabilities, IntelligenceType,
  MotionDetectionConfig, MotionZone,
  PerimeterConfig, DetectionRegion, Point,
  TripWireConfig, AreaDetectionConfig,
  PeopleCountingConfig, PrivacyMask,
} from '../types'

interface CommandResult {
  ok: boolean
  body?: string
  error?: string
}

/** Parse key=value plain text response into a map */
function parseKV(body: string): Record<string, string> {
  const map: Record<string, string> = {}
  for (const line of body.split(/\r?\n/)) {
    const eq = line.indexOf('=')
    if (eq > 0) map[line.slice(0, eq).trim()] = line.slice(eq + 1).trim()
  }
  return map
}

/** Send a CGI command to a camera via the server-side proxy */
export async function sendCommand(
  cameraId: string,
  module: string,
  params: string,
): Promise<CommandResult> {
  return apiFetch<CommandResult>('/api/camera/command', {
    method: 'POST',
    body: JSON.stringify({ cameraId, module, params }),
  })
}

// ── PTZ ──

export function ptzRotate(cameraId: string, pan: number, tilt: number) {
  return sendCommand(cameraId, 'ptz.cgi', `action=rotate&cameraID=1&pan=${pan}&tilt=${tilt}`)
}

export function ptzStop(cameraId: string) {
  return sendCommand(cameraId, 'ptz.cgi', 'action=stop&cameraID=1')
}

export function ptzZoom(cameraId: string, direction: 1 | -1) {
  return sendCommand(cameraId, 'ptz.cgi', `action=zoom&cameraID=1&pan=${direction}`)
}

export function ptzFocusFar(cameraId: string) {
  return sendCommand(cameraId, 'ptz.cgi', 'action=focusFar&cameraID=1')
}

export function ptzFocusNear(cameraId: string) {
  return sendCommand(cameraId, 'ptz.cgi', 'action=focusNear&cameraID=1')
}

export function ptzAutoFocus(cameraId: string) {
  return sendCommand(cameraId, 'ptz.cgi', 'action=runAutoFocus&cameraID=1')
}

export function ptzGoHome(cameraId: string) {
  return sendCommand(cameraId, 'ptz.cgi', 'action=goHome&cameraID=1')
}

export function ptzSetHome(cameraId: string) {
  return sendCommand(cameraId, 'ptz.cgi', 'action=setHome&cameraID=1')
}

// ── Presets ──

export function ptzListPresets(cameraId: string) {
  return sendCommand(cameraId, 'ptz.cgi', 'action=listPreset&cameraID=1')
}

export function ptzInvokePreset(cameraId: string, presetId: number) {
  return sendCommand(cameraId, 'ptz.cgi', `action=presetInvoke&cameraID=1&presetID=${presetId}`)
}

export function ptzAddPreset(cameraId: string, presetId: number, name: string) {
  return sendCommand(cameraId, 'ptz.cgi', `action=presetAdd&cameraID=1&presetID=${presetId}&presetName=${encodeURIComponent(name)}`)
}

export function ptzDeletePreset(cameraId: string, presetId: number) {
  return sendCommand(cameraId, 'ptz.cgi', `action=presetDelete&cameraID=1&presetID=${presetId}`)
}

// ── Wiper / Wash ──

export function wiperOpen(cameraId: string) {
  return sendCommand(cameraId, 'ptz.cgi', 'type=Wiper&action=open&cameraID=1')
}

export function wiperClose(cameraId: string) {
  return sendCommand(cameraId, 'ptz.cgi', 'type=Wiper&action=close&cameraID=1')
}

export function washOpen(cameraId: string) {
  return sendCommand(cameraId, 'ptz.cgi', 'type=Wash&action=open&cameraID=1')
}

// ── Lights ──

export function setIrLight(cameraId: string, on: boolean) {
  return sendCommand(cameraId, 'sensor.cgi', `action=set&type=infraredLight&cameraID=1&mode=${on ? 1 : 0}`)
}

export async function setWhiteLight(cameraId: string, on: boolean) {
  // Step 1: Read current dayNight state so we can change lightMode without losing dayNightMode
  const current = await sendCommand(cameraId, 'sensor.cgi', 'action=get&type=dayNight&cameraID=1&schemeID=0')

  const dnMatch = current.body?.match(/dayNightMode=(\d+)/)
  const dayNightMode = dnMatch ? dnMatch[1] : '0'

  // Step 2: Set lightMode=1 (White) or lightMode=0 (IR) alongside current dayNightMode
  // white=2 (Manual) required for WhiteLedManualMode to take effect; white=1 (Auto) ignores manual control
  const lightMode = on ? 1 : 0
  const whiteMode = on ? 2 : 1
  await sendCommand(cameraId, 'sensor.cgi', `action=set&type=dayNight&cameraID=1&schemeID=0&dayNightMode=${dayNightMode}&lightMode=${lightMode}&white=${whiteMode}`)

  // Step 3: Toggle the manual white LED
  return sendCommand(cameraId, 'param.cgi', `action=set&type=WhiteLedManualControl&cameraID=1&WhiteLedManualMode=${on ? 1 : 0}`)
}

// ── Day/Night ──

export function setDayNightMode(cameraId: string, mode: 0 | 1 | 2) {
  return sendCommand(cameraId, 'sensor.cgi', `action=set&type=dayNight&cameraID=1&schemeID=0&dayNightMode=${mode}`)
}

// ── Snapshot ──

export function getSnapshotUrl(cameraId: string): string {
  return `/api/camera/snapshot?cameraId=${cameraId}`
}

export async function downloadSnapshot(cameraId: string, cameraName: string) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) throw new Error('Not authenticated')

  const res = await fetch(getSnapshotUrl(cameraId), {
    headers: { Authorization: `Bearer ${session.access_token}` },
  })

  if (!res.ok) throw new Error('Snapshot failed')

  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${cameraName}-${new Date().toISOString().slice(0, 19).replace(/:/g, '')}.jpg`
  a.click()
  URL.revokeObjectURL(url)
}

// ── Recordings ──

export interface RecordingSegment {
  startTime: string  // YYYYMMDDHHMMSS
  endTime: string    // YYYYMMDDHHMMSS
  duration: number   // seconds
}

/** Convert a Date to camera timestamp format YYYYMMDDHHMMSS */
function toCameraTs(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`
}

/** Parse camera timestamp YYYYMMDDHHMMSS to Date */
export function parseCameraTs(ts: string): Date {
  const y = parseInt(ts.slice(0, 4))
  const m = parseInt(ts.slice(4, 6)) - 1
  const d = parseInt(ts.slice(6, 8))
  const h = parseInt(ts.slice(8, 10))
  const min = parseInt(ts.slice(10, 12))
  const s = parseInt(ts.slice(12, 14))
  return new Date(y, m, d, h, min, s)
}

export async function queryRecordings(
  cameraId: string,
  start: Date,
  end: Date,
): Promise<RecordingSegment[]> {
  const startTime = toCameraTs(start)
  const endTime = toCameraTs(end)
  const result = await apiFetch<{ ok: boolean; body: string }>(
    `/api/camera/recordings?cameraId=${cameraId}&action=query&startTime=${startTime}&endTime=${endTime}`,
  )

  if (!result.ok || !result.body) return []

  const segments: RecordingSegment[] = []
  const lines = result.body.split('\n')
  let current: Partial<RecordingSegment> = {}

  for (const line of lines) {
    const startMatch = line.match(/startTime=(\d{14})/)
    const endMatch = line.match(/endTime=(\d{14})/)

    if (startMatch) current.startTime = startMatch[1]
    if (endMatch) {
      current.endTime = endMatch[1]
      if (current.startTime && current.endTime) {
        const s = parseCameraTs(current.startTime)
        const e = parseCameraTs(current.endTime)
        segments.push({
          startTime: current.startTime,
          endTime: current.endTime,
          duration: Math.round((e.getTime() - s.getTime()) / 1000),
        })
        current = {}
      }
    }
  }

  return segments
}

export async function downloadRecording(
  cameraId: string,
  cameraName: string,
  startTime: string,
  endTime: string,
) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) throw new Error('Not authenticated')

  const url = `/api/camera/recordings?cameraId=${cameraId}&action=download&startTime=${startTime}&endTime=${endTime}`
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${session.access_token}` },
  })

  if (!res.ok) throw new Error('Download failed')

  const blob = await res.blob()
  const blobUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = blobUrl
  a.download = `${cameraName}-${startTime}-${endTime}.mp4`
  a.click()
  URL.revokeObjectURL(blobUrl)
}

// ── Camera Capability Probe ──

export async function probeCapabilities(cameraId: string): Promise<CameraCapabilities> {
  const [ptzRes, intRes, devRes] = await Promise.allSettled([
    sendCommand(cameraId, 'ptz.cgi', 'action=get&type=ptzCap&cameraID=1'),
    sendCommand(cameraId, 'param.cgi', 'action=get&type=getSupportIntelligences&cameraID=1'),
    sendCommand(cameraId, 'param.cgi', 'action=get&type=deviceInfo'),
  ])

  // PTZ capabilities — default true; only disable if camera explicitly reports ptzSupport=0
  let ptz = true
  let presetMaxNum = 0
  if (ptzRes.status === 'fulfilled' && ptzRes.value.ok && ptzRes.value.body) {
    const kv = parseKV(ptzRes.value.body)
    if (kv.ptzSupport === '0') ptz = false
    presetMaxNum = parseInt(kv.presetMaxNum ?? '0') || 0
  }

  // Supported intelligences
  const intelligences: IntelligenceType[] = []
  if (intRes.status === 'fulfilled' && intRes.value.ok && intRes.value.body) {
    const matches = intRes.value.body.matchAll(/intelligenceAnalyse=(\d+)/g)
    for (const m of matches) {
      intelligences.push(parseInt(m[1]) as IntelligenceType)
    }
  }

  // Device info
  let deviceModel: string | undefined
  let firmwareVersion: string | undefined
  if (devRes.status === 'fulfilled' && devRes.value.ok && devRes.value.body) {
    const kv = parseKV(devRes.value.body)
    deviceModel = kv.deviceName || kv.productModel
    firmwareVersion = kv.firmwareVersion || kv.softwareVersion
  }

  return { ptz, presetMaxNum, intelligences, deviceModel, firmwareVersion }
}

// ── Motion Detection ──

export async function getMotionDetection(cameraId: string): Promise<MotionDetectionConfig> {
  const res = await sendCommand(cameraId, 'param.cgi', 'action=get&type=motionAlarm&cameraID=1')
  if (!res.ok || !res.body) return { enabled: false, sensitivity: 5, zones: [] }

  const kv = parseKV(res.body)
  const enabled = kv.motionDetectionEnableFlag === '1'
  const sensitivity = parseInt(kv.sensitivity ?? '5') || 5

  // Parse zones from loop body
  const zones: MotionZone[] = []
  const lines = res.body.split(/\r?\n/)
  let zone: Partial<MotionZone> = {}
  for (const line of lines) {
    const m = line.match(/^(\w+)=(.+)$/)
    if (!m) continue
    const [, key, val] = m
    if (key === 'topX') zone.topX = parseInt(val)
    if (key === 'topY') zone.topY = parseInt(val)
    if (key === 'width') zone.width = parseInt(val)
    if (key === 'height') zone.height = parseInt(val)
    if (key.startsWith('next_motionDetectionAreaURL') || key === 'motionDetectionAreaEnd') {
      if (zone.topX !== undefined && zone.topY !== undefined && zone.width && zone.height) {
        zones.push(zone as MotionZone)
      }
      zone = {}
    }
  }
  // Catch last zone if no end marker
  if (zone.topX !== undefined && zone.topY !== undefined && zone.width && zone.height) {
    zones.push(zone as MotionZone)
  }

  return { enabled, sensitivity, zones }
}

export function setMotionDetection(cameraId: string, config: MotionDetectionConfig) {
  let params = `action=set&type=motionAlarm&cameraID=1&motionDetectionEnableFlag=${config.enabled ? 1 : 0}&sensitivity=${config.sensitivity}`

  if (config.zones.length > 0) {
    params += `&motionDetectionAction=cover&motionDetectionAreaCount=${config.zones.length}`
    config.zones.forEach((z, i) => {
      const prefix = i === 0 ? '&motionDetectionAreaBegin=1' : `&next_motionDetectionAreaURL=${i + 1}`
      params += `${prefix}&topX=${z.topX}&topY=${z.topY}&width=${z.width}&height=${z.height}`
    })
    params += `&motionDetectionAreaEnd=${config.zones.length}`
  }

  return sendCommand(cameraId, 'param.cgi', params)
}

// ── Perimeter / Intrusion Detection ──

function parseRegions(body: string): DetectionRegion[] {
  const regions: DetectionRegion[] = []
  let currentId = 0
  let points: Point[] = []
  let inRegion = false

  for (const line of body.split(/\r?\n/)) {
    const m = line.match(/^(\w+)=(.+)$/)
    if (!m) continue
    const [, key, val] = m

    if (key === 'regionBegin' || key === 'regionID') {
      if (inRegion && points.length > 0) {
        regions.push({ id: currentId, enabled: true, points })
      }
      currentId = parseInt(val) || regions.length
      points = []
      inRegion = true
    }
    if (key === 'pointX') {
      const px = parseFloat(val)
      // Look ahead — pointY should be next but we'll collect it
      points.push({ x: px, y: 0 })
    }
    if (key === 'pointY' && points.length > 0) {
      points[points.length - 1].y = parseFloat(val)
    }
    if (key === 'regionEnd' || key.startsWith('next_regionURL')) {
      if (points.length > 0) {
        regions.push({ id: currentId, enabled: true, points })
      }
      points = []
      inRegion = false
    }
  }
  if (inRegion && points.length > 0) {
    regions.push({ id: currentId, enabled: true, points })
  }
  return regions
}

function encodeRegions(regions: DetectionRegion[]): string {
  let params = `&regionCount=${regions.length}`
  regions.forEach((r, i) => {
    params += i === 0 ? `&regionBegin=1` : `&next_regionURL=${i + 1}`
    params += `&regionID=${r.id}&edgeCount=${r.points.length}`
    r.points.forEach(p => {
      params += `&pointX=${p.x.toFixed(6)}&pointY=${p.y.toFixed(6)}`
    })
  })
  if (regions.length > 0) params += `&regionEnd=${regions.length}`
  return params
}

export async function getPerimeter(cameraId: string): Promise<PerimeterConfig> {
  const res = await sendCommand(cameraId, 'param.cgi', 'action=get&type=perimeterParam&cameraID=1')
  if (!res.ok || !res.body) return { enabled: false, sensitivity: 3, targetType: 0, regions: [] }

  const kv = parseKV(res.body)
  return {
    enabled: kv.enableFlag === '1',
    sensitivity: parseInt(kv.sensitivity ?? '3') || 3,
    targetType: parseInt(kv.targetType ?? '0') || 0,
    regions: parseRegions(res.body),
  }
}

export function setPerimeter(cameraId: string, config: PerimeterConfig) {
  let params = `action=set&type=perimeterParam&cameraID=1&enableFlag=${config.enabled ? 1 : 0}&sensitivity=${config.sensitivity}&targetTypeEnable=1&targetType=${config.targetType}`
  params += encodeRegions(config.regions)
  return sendCommand(cameraId, 'param.cgi', params)
}

// ── TripWire (Line Crossing) ──

export async function getTripWire(cameraId: string): Promise<TripWireConfig> {
  const res = await sendCommand(cameraId, 'param.cgi', 'action=get&type=tripWireParam&cameraID=1')
  if (!res.ok || !res.body) return { enabled: false, targetType: 0, lines: [] }

  const kv = parseKV(res.body)
  const lines: TripWireConfig['lines'] = []

  let lineId = 0
  let current: { startX?: number; startY?: number; endX?: number; endY?: number } = {}
  for (const line of res.body.split(/\r?\n/)) {
    const m = line.match(/^(\w+)=(.+)$/)
    if (!m) continue
    const [, key, val] = m
    if (key === 'regionBegin' || key === 'regionID') {
      lineId = parseInt(val) || lines.length
      current = {}
    }
    if (key === 'lineCrossStartX') current.startX = parseFloat(val)
    if (key === 'lineCrossStartY') current.startY = parseFloat(val)
    if (key === 'lineCrossEndX') current.endX = parseFloat(val)
    if (key === 'lineCrossEndY') current.endY = parseFloat(val)
    if (key === 'regionEnd' || key.startsWith('next_regionURL')) {
      if (current.startX !== undefined && current.endX !== undefined) {
        lines.push({ id: lineId, startX: current.startX!, startY: current.startY!, endX: current.endX!, endY: current.endY! })
      }
      current = {}
    }
  }
  if (current.startX !== undefined && current.endX !== undefined) {
    lines.push({ id: lineId, startX: current.startX!, startY: current.startY!, endX: current.endX!, endY: current.endY! })
  }

  return {
    enabled: kv.enableFlag === '1',
    targetType: parseInt(kv.targetType ?? '0') || 0,
    lines,
  }
}

export function setTripWire(cameraId: string, config: TripWireConfig) {
  let params = `action=set&type=tripWireParam&cameraID=1&enableFlag=${config.enabled ? 1 : 0}&targetTypeEnable=1&targetType=${config.targetType}`
  params += `&regionCount=${config.lines.length}`
  config.lines.forEach((l, i) => {
    params += i === 0 ? `&regionBegin=1` : `&next_regionURL=${i + 1}`
    params += `&regionID=${l.id}&lineCrossStartX=${l.startX.toFixed(6)}&lineCrossStartY=${l.startY.toFixed(6)}&lineCrossEndX=${l.endX.toFixed(6)}&lineCrossEndY=${l.endY.toFixed(6)}`
  })
  if (config.lines.length > 0) params += `&regionEnd=${config.lines.length}`
  return sendCommand(cameraId, 'param.cgi', params)
}

// ── Enter / Leave Area ──

export async function getAreaDetection(cameraId: string, type: 'enterArea' | 'leaveArea'): Promise<AreaDetectionConfig> {
  const res = await sendCommand(cameraId, 'param.cgi', `action=get&type=${type}Param&cameraID=1`)
  if (!res.ok || !res.body) return { enabled: false, targetType: 0, regions: [] }

  const kv = parseKV(res.body)
  return {
    enabled: kv.enableFlag === '1',
    targetType: parseInt(kv.targetType ?? '0') || 0,
    regions: parseRegions(res.body),
  }
}

export function setAreaDetection(cameraId: string, type: 'enterArea' | 'leaveArea', config: AreaDetectionConfig) {
  let params = `action=set&type=${type}Param&cameraID=1&enableFlag=${config.enabled ? 1 : 0}&targetTypeEnable=1&targetType=${config.targetType}`
  params += encodeRegions(config.regions)
  return sendCommand(cameraId, 'param.cgi', params)
}

// ── People Counting ──

export async function getPeopleCounting(cameraId: string): Promise<PeopleCountingConfig> {
  const res = await sendCommand(cameraId, 'param.cgi', 'action=get&type=getStatisticsCfg&cameraID=1')
  if (!res.ok || !res.body) return { enabled: false, osdEnabled: false, regionType: 1, alarmEnabled: false, alarmThreshold: 0 }

  const kv = parseKV(res.body)
  const config: PeopleCountingConfig = {
    enabled: kv.enableFlag === '1',
    osdEnabled: kv.OSDEnable === '1',
    regionType: parseInt(kv.RegionType ?? '1') || 1,
    alarmEnabled: kv.AlarmEnable === '1',
    alarmThreshold: parseInt(kv.AlarmThreshold ?? '0') || 0,
  }

  if (kv.lineCrossStartX !== undefined) {
    config.line = {
      startX: parseFloat(kv.lineCrossStartX),
      startY: parseFloat(kv.lineCrossStartY ?? '0'),
      endX: parseFloat(kv.lineCrossEndX ?? '0'),
      endY: parseFloat(kv.lineCrossEndY ?? '0'),
    }
  }

  return config
}

export function setPeopleCounting(cameraId: string, config: PeopleCountingConfig) {
  let params = `action=set&type=setStatisticsCfg&cameraID=1&enableFlag=${config.enabled ? 1 : 0}&OSDEnable=${config.osdEnabled ? 1 : 0}&RegionType=${config.regionType}&AlarmEnable=${config.alarmEnabled ? 1 : 0}&AlarmThreshold=${config.alarmThreshold}`
  if (config.line) {
    params += `&lineCrossStartX=${config.line.startX.toFixed(6)}&lineCrossStartY=${config.line.startY.toFixed(6)}&lineCrossEndX=${config.line.endX.toFixed(6)}&lineCrossEndY=${config.line.endY.toFixed(6)}`
  }
  return sendCommand(cameraId, 'param.cgi', params)
}

// ── Privacy Mask ──

export async function getPrivacyMasks(cameraId: string): Promise<PrivacyMask[]> {
  const res = await sendCommand(cameraId, 'param.cgi', 'action=get&type=blindArea&cameraID=1')
  if (!res.ok || !res.body) return []

  const masks: PrivacyMask[] = []
  let current: Partial<PrivacyMask> = {}

  for (const line of res.body.split(/\r?\n/)) {
    const m = line.match(/^(\w+)=(.+)$/)
    if (!m) continue
    const [, key, val] = m
    if (key === 'areaID') current.areaId = parseInt(val)
    if (key === 'enableFlag') current.enabled = val === '1'
    if (key === 'topX') current.topX = parseInt(val)
    if (key === 'topY') current.topY = parseInt(val)
    if (key === 'width') current.width = parseInt(val)
    if (key === 'height') current.height = parseInt(val)
    if (key === 'blindType') current.blindType = parseInt(val)
    if (key === 'blindColor') current.blindColor = val
    if (key.startsWith('next_') || key === 'BlindAreaParamEnd') {
      if (current.areaId !== undefined) {
        masks.push({
          areaId: current.areaId,
          enabled: current.enabled ?? true,
          topX: current.topX ?? 0,
          topY: current.topY ?? 0,
          width: current.width ?? 0,
          height: current.height ?? 0,
          blindType: current.blindType ?? 1,
          blindColor: current.blindColor ?? '000000',
        })
      }
      current = {}
    }
  }
  if (current.areaId !== undefined) {
    masks.push({
      areaId: current.areaId,
      enabled: current.enabled ?? true,
      topX: current.topX ?? 0,
      topY: current.topY ?? 0,
      width: current.width ?? 0,
      height: current.height ?? 0,
      blindType: current.blindType ?? 1,
      blindColor: current.blindColor ?? '000000',
    })
  }

  return masks
}

export function setPrivacyMask(cameraId: string, mask: PrivacyMask) {
  return sendCommand(cameraId, 'param.cgi',
    `action=set&type=blindArea&cameraID=1&areaParamAction=add&areaID=${mask.areaId}&enableFlag=${mask.enabled ? 1 : 0}&topX=${mask.topX}&topY=${mask.topY}&width=${mask.width}&height=${mask.height}&blindType=${mask.blindType}&blindColor=${mask.blindColor}`)
}

export function deletePrivacyMask(cameraId: string, areaId?: number) {
  const params = areaId !== undefined
    ? `action=delete&type=blindArea&cameraID=1&areaID=${areaId}`
    : `action=delete&type=blindArea&cameraID=1`
  return sendCommand(cameraId, 'param.cgi', params)
}

// ── Alarm Center (AI Event Push) ──

export interface AlarmCenterConfig {
  serverIp: string
  serverPort: number
  account: string
  password: string
  urlStart: string
  urlEnd: string
}

export async function getAlarmCenter(cameraId: string): Promise<AlarmCenterConfig | null> {
  const res = await sendCommand(cameraId, 'param.cgi', 'action=get&type=getAlarmCenter&cameraID=1')
  if (!res.ok || !res.body) return null

  const kv = parseKV(res.body)
  return {
    serverIp: kv.alarmCenterServerIP ?? '',
    serverPort: parseInt(kv.alarmCenterServerPort ?? '0') || 0,
    account: kv.alarmCenterAccount ?? '',
    password: kv.alarmCenterPassword ?? '',
    urlStart: kv.alarmCenterUrlStart ?? '',
    urlEnd: kv.alarmCenterUrlEnd ?? '',
  }
}

/**
 * Configure the camera's alarm center to push AI events to our cloud endpoint.
 * This tells the camera: "POST detection events to https://redvisioncloud.com/api/camera/alarm-event?cameraId=X"
 *
 * The camera will then automatically send face/body/vehicle/plate detections
 * as HTTP POST requests whenever its AI triggers.
 */
export async function configureAlarmCenter(cameraId: string): Promise<CommandResult> {
  // Our Cloudflare Pages endpoint that receives alarm events
  const host = location.host // e.g. redvisioncloud.com
  const port = location.protocol === 'https:' ? 443 : 80
  const url = `/api/camera/alarm-event?cameraId=${encodeURIComponent(cameraId)}`

  const params = [
    'action=set',
    'type=setAlarmCenter',
    'cameraID=1',
    `alarmCenterServerIP=${host}`,
    `alarmCenterServerPort=${port}`,
    `alarmCenterUrlStart=${encodeURIComponent(url)}`,
    `alarmCenterUrlEnd=${encodeURIComponent(url)}`,
  ].join('&')

  return sendCommand(cameraId, 'param.cgi', params)
}

// ── People Counting Stats (poll from camera) ──

export async function pollPeopleCountingStats(
  cameraId: string,
  queryType: 1 | 2 | 3 | 4 = 4,
  year?: number,
  month?: number,
  day?: number,
): Promise<{ ok: boolean; count: number }> {
  const res = await apiFetch<{ ok: boolean; count: number }>('/api/camera/poll-stats', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cameraId, queryType, year, month, day }),
  })
  return res
}
